import { userService } from './userService';

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
export const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

export const getAllChannels = async () => {
  const jwtToken = await userService.getAuthToken();

  const response = await fetch(`${API_BASE_URL}/channels`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${jwtToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch channels: ${response.status}`);
  }

  const data = await response.json();
  // Backend returns { channels: [...], count: N }
  // Be defensive in case of legacy array response
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.channels)) {
    return data.channels;
  }
  return [];
};

export const channelService = {
  getAllChannels,
  async disconnectOutlook(): Promise<any> {
    try {
      const jwtToken = await userService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };

      const response = await fetch(`${API_BASE_URL}/channels/outlook/disconnect`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to disconnect Outlook: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error disconnecting Outlook:', error);
      throw error;
    }
  },
  async disconnectGmail(): Promise<any> {
    try {
      const jwtToken = await userService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };

      const response = await fetch(`${API_BASE_URL}/gmail/disconnect`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to disconnect Gmail: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error disconnecting Gmail:', error);
      throw error;
    }
  },
  async disconnectInstagram(): Promise<any> {
    try {
      const jwtToken = await userService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };

      const response = await fetch(`${API_BASE_URL}/instagram/disconnect`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to disconnect Instagram: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error disconnecting Instagram:', error);
      throw error;
    }
  },
  async disconnectShopify(shopDomain: string): Promise<any> {
    try {
      const jwtToken = await userService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };

      const url = `${API_BASE_URL}/shopify/disconnect?shop=${encodeURIComponent(shopDomain)}`;
      const response = await fetch(url, { method: 'DELETE', headers });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to disconnect Shopify: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error disconnecting Shopify:', error);
      throw error;
    }
  },
  async removeChannel(channelId: string): Promise<any> {
    try {
      const jwtToken = await userService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      };
      
      const response = await fetch(`${API_BASE_URL}/channels/${channelId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to remove channel: ${response.status} - ${errorText}`);
      }

      // Return the response data which includes cleanup_summary
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing channel:', error);
      throw error;
    }
  }
}; 